<script lang="ts">
import { defineComponent, ref, computed, watch, onBeforeMount, unref, onBeforeUnmount } from 'vue';

import { convertRemToPx } from '@components/utils';

import {
  makeInvertColor,
  makeTagProps,
  makeThemeProps,
  makeDimensionProps,
  provideTheme,
  useInvertColor,
  useDisplay,
  useProxyModel,
  useDimensions
} from '../../composable';
import { useInjectLayout } from '../CdsLayout/context';

import styles from './CdsDrawer.module.scss';

import type { PropType, CSSProperties } from 'vue';

export const CDS_DRAWER_WIDTH = convertRemToPx(styles['cds-drawer-width']);
export const CDS_DRAWER_WIDTH_MINIFIED = convertRemToPx(styles['cds-drawer-width-minified']);
export const CDS_DRAWER_Z_INDEX_INNER = Number(styles['cds-drawer-z-index-inner']);
export const CDS_DRAWER_Z_INDEX_OUTER = Number(styles['cds-drawer-z-index-outer']) + 1;

export const componentName = 'CdsDrawer';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Показать/Скрыть
     */
    modelValue: {
      type: Boolean as PropType<boolean | null>,
      default: null
    },
    /**
     * Видимый всегда, в независимости от размера экрана
     */
    permanent: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Временный, показывается при изменении v-model и использует overlay для закрытия
     */
    temporary: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Уменьшенный вариант отображение (только иконки) до наведения мыши
     */
    minified: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Применяет position: fixed
     */
    fixed: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Позиционирование справа
     */
    right: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Добавляет границы между элементами
     */
    border: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Использовать fixed для слотов c внутренним скроллом
     */
    fixedSlots: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    ...makeDimensionProps(),
    ...makeInvertColor(),
    ...makeThemeProps(),
    ...makeTagProps({
      tag: 'aside'
    })
  },
  emits: {
    /**
     * Событие открытия
     * @type { boolean }
     */
    opened: (value: boolean) => true,
    /**
     * Событие закрытия
     * @type { boolean }
     */
    closed: (value: boolean) => true,
    /**
     * Событие изменения v-model
     * @type { boolean }
     */
    'update:modelValue': (value: boolean) => true
  },
  setup(props, { emit, slots, attrs, expose }) {
    const drawerRef = ref<HTMLDivElement>();
    const isActive = useProxyModel(props, 'modelValue', null, (value) => !!value);
    const isTemporary = computed(() => !props.permanent && props.temporary);
    const isHovering = ref(false);
    const display = useDisplay();
    const layout = useInjectLayout();
    const { dimensionsStyles } = useDimensions(props);
    const inLayout = ref(!!Object.keys(layout).length);
    const { themeClasses } = provideTheme(props);

    const fixed = computed(() => {
      if (!unref(inLayout)) return props.fixed;

      return (
        props.fixed ||
        unref(layout.view).indexOf(props.right ? 'R' : 'L') > -1 ||
        (unref(display.platform).ios && unref(layout.isContainer))
      );
    });

    const { invertColorClasses } = useInvertColor(props, componentName);
    const drawerClasses = computed(() => {
      return {
        'cds-drawer--active': unref(isActive),
        'cds-drawer--hover': unref(isHovering),
        'cds-drawer--temporary': unref(isTemporary),
        'cds-drawer--minified': props.minified,
        'cds-drawer--right': props.right,
        'cds-drawer--fixed': unref(fixed),
        'cds-drawer--border': props.border,
        'cds-drawer--fixed-slots': props.fixedSlots
      };
    });

    const headerSlot = computed(() => {
      if (!unref(inLayout)) return false;
      return props.right ? unref(layout.rows).top[2] === 'r' : unref(layout.rows).top[0] === 'l';
    });

    const footerSlot = computed(() => {
      if (!unref(inLayout)) return false;
      return props.right ? unref(layout.rows).bottom[2] === 'r' : unref(layout.rows).bottom[0] === 'l';
    });

    const styles = computed(() => {
      const css: CSSProperties = { ...unref(dimensionsStyles) };

      if (unref(inLayout)) {
        if (layout.header.space && !unref(headerSlot)) {
          if (unref(fixed)) {
            css.top = layout.header.offset + 'px';
            css.height = `calc(100% - ${layout.header.offset}px)`;
          } else if (layout.header.space) {
            css.top = layout.header.size + 'px';
            css.height = `calc(100% - ${layout.header.size}px)`;
          }
        }

        if (layout.footer.space && !unref(footerSlot)) {
          if (unref(fixed)) {
            css.bottom = layout.footer.offset + 'px';
            css.height = `calc(100% - ${layout.footer.offset}px - ${layout.header.offset}px)`;
          } else if (layout.footer.space) {
            css.bottom = layout.footer.size + 'px';
            css.height = `calc(100% - ${layout.footer.size}px - ${layout.header.size}px)`;
          }
        }

        if (unref(headerSlot)) {
          if (layout.footer.space) {
            css.height = `calc(100% - ${layout.footer.size}px)`;

            if (props.minified) {
              css.zIndex = CDS_DRAWER_Z_INDEX_OUTER;
            }
          }
        }

        if (unref(footerSlot)) {
          if (layout.header.space) {
            css.height = `calc(100% - ${layout.header.size}px)`;

            if (props.minified) {
              css.zIndex = CDS_DRAWER_Z_INDEX_OUTER;
            }
          }
        }

        if (unref(headerSlot) && unref(footerSlot)) {
          css.height = '100%';
          css.zIndex = CDS_DRAWER_Z_INDEX_OUTER;
        }
      }

      return css;
    });

    const hasOverlay = computed(() => props.modelValue);
    const hasPrependSlot = computed((): boolean => !!slots.prepend);
    const hasAppendSlot = computed((): boolean => !!slots.append);

    let activeElement: HTMLElement | null = null;

    onBeforeMount(() => {
      if (props.modelValue !== null || unref(isTemporary)) return;
      isActive.value = props.permanent;
    });

    onBeforeUnmount(() => {
      if (unref(inLayout)) {
        layout.update(props.right ? 'right' : 'left', 'space', false);
      }
    });

    watch(
      () => unref(isActive),
      (value) => {
        if (value) {
          activeElement = document.activeElement as HTMLElement;
          emit('opened', value);
        } else {
          if (activeElement) {
            activeElement.focus();
          }
          emit('closed', value);
        }
      }
    );

    if (unref(inLayout)) {
      watch(
        display.mdUp,
        (value) => {
          layout.update(props.right ? 'right' : 'left', 'space', props.temporary ? false : value);
        },
        { immediate: true }
      );

      watch(
        () => props.minified,
        (value) => {
          layout.update(
            props.right ? 'right' : 'left',
            'size',
            props.minified ? CDS_DRAWER_WIDTH_MINIFIED : CDS_DRAWER_WIDTH
          );
        },
        { immediate: true }
      );

      watch(
        () => props.temporary,
        (value) => {
          layout.update(props.right ? 'right' : 'left', 'space', !value);
        },
        { immediate: true }
      );
    }

    const onClose = () => {
      isActive.value = false;
    };

    const onMouseenter = () => {
      isHovering.value = true;
    };

    const onMouseleave = () => {
      isHovering.value = false;
    };

    expose({
      drawerRef
    });

    return {
      attrs,
      drawerRef,
      hasPrependSlot,
      hasAppendSlot,
      hasOverlay,
      drawerClasses,
      invertColorClasses,
      themeClasses,
      styles,
      onClose,
      onMouseenter,
      onMouseleave
    };
  }
});
</script>

<template>
  <component
    :is="tag"
    ref="drawerRef"
    class="cds-drawer"
    :class="[drawerClasses, invertColorClasses, themeClasses]"
    :style="styles"
    tabindex="-1"
    v-bind="attrs"
    @keydown.esc="onClose"
    @mouseenter="onMouseenter"
    @mouseleave="onMouseleave"
  >
    <div v-if="hasPrependSlot" class="cds-drawer__prepend">
      <!--
        @slot Слот для контента перед элементами навигацией
          @type html | vue | CdsList
      -->
      <slot name="prepend" v-bind="{ invertColor }" />
    </div>
    <nav class="cds-drawer__content">
      <!--
        @slot Слот для элементов навигации
          @type CdsList | CdsListItem | CdsListGroup
      -->
      <slot v-bind="{ invertColor }" />
    </nav>
    <div v-if="hasAppendSlot" class="cds-drawer__append">
      <!--
        @slot Слот для контента после элементов навигацией
          @type html | vue | CdsList
      -->
      <slot name="append" v-bind="{ invertColor }" />
    </div>
  </component>
  <div v-if="hasOverlay" class="cds-drawer__overlay" @click="onClose" />
</template>

<style lang="scss">
@import './CdsDrawer';
</style>
